import { SearchItem, SearchResult, SearchOptions } from './types';

export class SearchService {
  private config: SearchOptions['config'];
  private defaultPageSize: number;
  private timeout: number;
  private searchFields: string[];
  private sortFields: SearchOptions['sortFields'];
  private dataStructure: 'simple' | 'elastic_document';

  constructor(options: SearchOptions) {
    this.config = options.config;
    this.defaultPageSize = options.pageSize || 24;
    this.timeout = options.timeout || 7000;
    this.dataStructure = options.dataStructure || 'simple';
    this.searchFields = options.searchFields || [
      "name^3",
      "code^2",
      "languages.ru.name^3",
      "languages.en.name^3",
      "languages.de.name^3",
      "languages.ru.description^2",
      "languages.en.description^2",
      "languages.de.description^2",
      "taxonomy.category^2",
      "taxonomy.chain^2",
      "sku^2",
      "slug^2"
    ];
    this.sortFields = options.sortFields || {
      price_asc: 'facets.price',
      price_desc: 'facets.price',
      new: 'system.indexed_at',
      relevance: '_score'
    };
  }

  // Никаких значений по умолчанию для имени индекса: стенд общий для нескольких
  // проектов, а пустое имя превращает URL в `${node}//_search` — это валидный
  // запрос ко ВСЕМ индексам стенда, то есть выдача чужого проекта под видом своей.
  private searchUrl(): string {
    if (!this.config?.node || !this.config?.index) {
      throw new Error('[SearchService] Elasticsearch is not configured: node and index are required');
    }

    return `${this.config.node}/${this.config.index}/_search`;
  }

  async searchItems(category: string, query: string, langCode?: string): Promise<SearchResult> {
    try {
      // Разбираем строку запроса для извлечения параметров
      const urlParams = new URLSearchParams(query);
      console.log('URL params:', query);
      console.log('Parsed URL params:', Object.fromEntries(urlParams.entries()));

      // Поддержка как q, так и query параметров
      const searchQuery = urlParams.get('q') || urlParams.get('query');
      const decodedSearchQuery = searchQuery ? decodeURIComponent(searchQuery) : null;
      const sortParam = urlParams.get('sort') ?? '';

      const mustClauses: any[] = [];

      if (decodedSearchQuery) {
        console.log('Search query (raw):', searchQuery);
        console.log('Search query (decoded):', decodedSearchQuery);
        mustClauses.push({
          "multi_match": {
            "query": decodedSearchQuery,
            "fields": this.searchFields,
            "type": "best_fields",
            "fuzziness": "AUTO",
          },
        });
      }

      // Обработка фильтров
      urlParams.forEach((value, key) => {
        if (!value || key === 'page' || key === 'query' || key === 'q' || key === 'sort') {
          return;
        }

        const values = value.split(',').filter((entry) => entry.trim()).map((entry) => entry.trim());
        if (values.length === 0) {
          return;
        }

        const baseFacetField = this.dataStructure === 'elastic_document' 
          ? `elastic_document.facets.${key}`
          : `facets.${key}`;
        const stringValues: string[] = [];
        const numericValues: number[] = [];
        const booleanValues: boolean[] = [];

        values.forEach((candidate) => {
          if (candidate === 'true' || candidate === 'false') {
            booleanValues.push(candidate === 'true');
            return;
          }

          const numericCandidate = Number(candidate);
          if (!Number.isNaN(numericCandidate) && candidate !== '') {
            numericValues.push(numericCandidate);
            return;
          }

          stringValues.push(candidate);
        });

        const shouldClauses: any[] = [];

        if (stringValues.length > 0) {
          shouldClauses.push({
            terms: {
              [`${baseFacetField}.keyword`]: stringValues,
            },
          });
          shouldClauses.push({
            terms: {
              [`${key}.keyword`]: stringValues,
            },
          });
        }

        if (numericValues.length > 0) {
          shouldClauses.push({
            terms: {
              [baseFacetField]: numericValues,
            },
          });
          shouldClauses.push({
            terms: {
              [key]: numericValues,
            },
          });
        }

        if (booleanValues.length > 0) {
          shouldClauses.push({
            terms: {
              [baseFacetField]: booleanValues,
            },
          });
          shouldClauses.push({
            terms: {
              [key]: booleanValues,
            },
          });
        }

        if (shouldClauses.length > 0) {
          mustClauses.push({
            bool: {
              should: shouldClauses,
              minimum_should_match: 1,
            },
          });
        }
      });

      const page = urlParams.get('page') ? parseInt(urlParams.get('page') as string) : 1;

      // Используем AbortController для установки таймаута запроса
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      try {
        // Выполняем запрос к Elasticsearch
        const requestBody: any = {
          query: {
            bool: {
              must: mustClauses.length > 0 ? mustClauses : [{ match_all: {} }],
            },
          },
          sort: this.getSortClause(sortParam, langCode),
          from: (page - 1) * this.defaultPageSize,
          size: this.defaultPageSize,
          track_total_hits: true,
          _source: this.dataStructure === 'elastic_document' ? [
            "id",
            "name",
            "code",
            "picture",
            "price",
            "priceOld",
            "sectionCode",
            "BREND",
            "ATTR_METAL",
            "labels",
            "elastic_document.labels",
            "elastic_document.languages",
            "elastic_document.media",
            "elastic_document.facets",
            "elastic_document.attributes",
            "elastic_document.taxonomy",
            "elastic_document.system",
            "elastic_document.core",
            "image_gallery"
          ] : [
            "id",
            "name",
            "code",
            "picture",
            "price",
            "priceOld",
            "sectionCode",
            "BREND",
            "ATTR_METAL",
            "labels",
            "languages",
            "media",
            "facets",
            "attributes",
            "taxonomy",
            "system",
            "image_gallery"
          ],
        };

        console.log('Elasticsearch request body:', JSON.stringify(requestBody, null, 2));

        const response = await fetch(this.searchUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': this.config.auth,
          },
          body: JSON.stringify(requestBody),
          signal: controller.signal,
        });

        // Очищаем таймаут после завершения запроса
        clearTimeout(timeoutId);

        if (!response.ok) {
          console.error(`Ошибка Elasticsearch: ${response.status} ${response.statusText}`);
          return {
            item: [],
            totalItems: 0,
            error: `Ошибка сервера: ${response.status}`
          };
        }

        const searchResult = await response.json();

        // Отладочная информация
        console.log('Search result:', {
          total: searchResult.hits?.total?.value,
          hitsCount: searchResult.hits?.hits?.length,
          firstHit: searchResult.hits?.hits?.[0]?._source,
          took: searchResult.took,
          timed_out: searchResult.timed_out
        });

        // Проверка наличия ошибок в ответе Elasticsearch
        if (searchResult.error) {
          console.error('Ошибка Elasticsearch:', searchResult.error);
          return {
            item: [],
            totalItems: 0,
            error: 'Ошибка поиска товаров'
          };
        }

        // Проверяем наличие searchResult.hits и searchResult.hits.hits
        if (!searchResult.hits || !Array.isArray(searchResult.hits.hits)) {
          console.error('Неверная структура ответа Elasticsearch');
          return {
            item: [],
            totalItems: 0,
            error: 'Неверная структура ответа'
          };
        }

        // Получаем список товаров с маппингом данных для ProductCard
        const itemsShop = searchResult.hits.hits.map((hit: any) => this.mapElasticsearchHit(hit, langCode));

        // Получаем общее количество элементов, удовлетворяющих условиям поиска
        const totalItems = searchResult.hits.total?.value || 0;

        // Если нет результатов, попробуем более простой запрос
        if (totalItems === 0 && decodedSearchQuery) {
          return await this.performFallbackSearch(decodedSearchQuery, controller, langCode);
        }

        return {
          item: itemsShop,
          totalItems: totalItems
        };

      } catch (error: any) {
        if (error.name === 'AbortError') {
          console.error('Запрос к Elasticsearch был прерван из-за таймаута');
          return {
            item: [],
            totalItems: 0
          };
        }
        throw error;
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error) {
      console.error('Database Error:', error);
      return {
        item: [],
        totalItems: 0
      };
    }
  }

  private getSortClause(sortParam: string, langCode?: string): any[] {
    switch (sortParam) {
      case 'price_asc':
        return [
          {
            [langCode ? `languages.${langCode}.price` : (this.sortFields?.price_asc || 'price')]: {
              order: 'asc',
              unmapped_type: 'double',
              missing: '_last',
            },
          },
          { 'id.keyword': { order: 'desc', unmapped_type: 'keyword' } },
          { id: { order: 'desc', unmapped_type: 'long' } },
        ];
      case 'price_desc':
        return [
          {
            [langCode ? `languages.${langCode}.price` : (this.sortFields?.price_desc || 'price')]: {
              order: 'desc',
              unmapped_type: 'double',
              missing: '_last',
            },
          },
          { 'id.keyword': { order: 'desc', unmapped_type: 'keyword' } },
          { id: { order: 'desc', unmapped_type: 'long' } },
        ];
      case 'new':
        return [
          {
            [this.sortFields?.new || 'system.indexed_at']: {
              order: 'desc',
              unmapped_type: 'date',
              missing: '_last',
            },
          },
          { 'id.keyword': { order: 'desc', unmapped_type: 'keyword' } },
          { id: { order: 'desc', unmapped_type: 'long' } },
        ];
      default:
        return [
          { [this.sortFields?.relevance || '_score']: { order: 'desc' } },
        ];
    }
  }

  private mapElasticsearchHit(hit: any, langCode?: string): SearchItem {
    const source = hit._source;
    
    // Определяем базовый объект данных в зависимости от структуры
    const dataRoot = this.dataStructure === 'elastic_document' 
      ? source?.elastic_document || {}
      : source;
    
    const baseSource = this.dataStructure === 'elastic_document' 
      ? { ...source, ...dataRoot }
      : source;

    const languages = baseSource?.languages || dataRoot?.languages;
    const langData = langCode && languages && typeof languages === 'object' ? languages[langCode] : undefined;
    const localizedName =
      typeof langData?.name === 'string' && langData.name.trim()
        ? langData.name.trim()
        : baseSource.name || source.name;

    const localizedPrice =
      langCode ? (langData?.price ?? baseSource.price ?? source.price) : (baseSource.price ?? source.price);
    const localizedPriceOld =
      langCode ? (langData?.priceOld ?? baseSource.priceOld ?? source.priceOld) : (baseSource.priceOld ?? source.priceOld);

    const mediaCover = typeof baseSource?.media?.cover === 'string' 
      ? baseSource.media.cover 
      : (typeof dataRoot?.media?.cover === 'string' ? dataRoot.media.cover : undefined);
    const imageGallery = Array.isArray(source?.image_gallery) ? source.image_gallery : [];
    const picture = source.picture || mediaCover || imageGallery[0];

    return {
      _elasticId: hit?._id,
      id: source.id,
      code: source.CML2_ARTICLE || source.code,
      name: localizedName || 'Товар',
      picture: picture || '',
      price: typeof localizedPrice === 'number' ? localizedPrice : Number(localizedPrice) || 0,
      priceOld: typeof localizedPriceOld === 'number' ? localizedPriceOld : Number(localizedPriceOld) || undefined,
      sectionCode: source.sectionCode,
      BREND: source.BREND,
      ATTR_METAL: source.ATTR_METAL,
      labels: baseSource.labels || source.labels,
      ...baseSource
    };
  }

  private async performFallbackSearch(searchQuery: string, controller: AbortController, langCode?: string): Promise<SearchResult> {
    console.log('No results found, trying fallback search...');
    
    try {
      const fallbackResponse = await fetch(this.searchUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': this.config.auth,
        },
        body: JSON.stringify({
          query: {
            multi_match: {
              query: searchQuery,
              fields: ["*"],
              type: "best_fields",
              fuzziness: "AUTO"
            }
          },
          size: this.defaultPageSize,
          track_total_hits: true,
          _source: this.dataStructure === 'elastic_document' ? [
            "id",
            "name",
            "code",
            "picture",
            "price",
            "priceOld",
            "sectionCode",
            "BREND",
            "ATTR_METAL",
            "labels",
            "elastic_document.labels",
            "elastic_document.languages",
            "elastic_document.media",
            "elastic_document.facets",
            "elastic_document.attributes",
            "elastic_document.taxonomy",
            "elastic_document.system",
            "elastic_document.core",
            "image_gallery"
          ] : [
            "id",
            "name",
            "code",
            "picture",
            "price",
            "priceOld",
            "sectionCode",
            "BREND",
            "ATTR_METAL",
            "labels",
            "languages",
            "media",
            "facets",
            "attributes",
            "taxonomy",
            "system",
            "image_gallery"
          ]
        }),
        signal: controller.signal,
      });

      if (fallbackResponse.ok) {
        const fallbackResult = await fallbackResponse.json();
        console.log('Fallback search result:', {
          total: fallbackResult.hits?.total?.value,
          hitsCount: fallbackResult.hits?.hits?.length
        });
        
        if (fallbackResult.hits?.hits?.length > 0) {
          const fallbackItems = fallbackResult.hits.hits.map((hit: any) => this.mapElasticsearchHit(hit, langCode));
          
          return {
            item: fallbackItems,
            totalItems: fallbackResult.hits.total?.value || 0
          };
        }
      }
    } catch (error) {
      console.error('Fallback search error:', error);
    }

    return {
      item: [],
      totalItems: 0
    };
  }
}
